import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import SEO from "../components/SEO";
import LeadCaptureModal from "../components/LeadCaptureModal";
import OpenInTabLink from "../components/common/OpenInTabLink";
import { getCompanySettings, buildProductEnquiryWaLink } from "../utils/whatsappHelper";
import {
  MagnifyingGlass, ShieldCheck, VideoCamera, HardDrives, Fingerprint, House,
  WhatsappLogo, PaperPlaneTilt, Phone, Eye, Funnel, X, Tag, Lightning, Star,
  SortAscending, GridNine, List, Wifi, ArrowRight
} from "@phosphor-icons/react";

const CATEGORY_ICONS = { VideoCamera, HardDrives, Fingerprint, House, ShieldCheck };
const RESOLUTIONS = ["2mp", "4mp", "5mp", "8mp", "4k"];
const PRODUCT_TYPES = [
  { v: "analog", label: "Analog" }, { v: "ip", label: "IP" }, { v: "wireless", label: "Wireless" },
  { v: "biometric", label: "Biometric" }, { v: "automation", label: "Automation" }, { v: "accessory", label: "Accessory" },
];
const CAMERA_TYPES = [
  { v: "dome", label: "Dome" }, { v: "bullet", label: "Bullet" }, { v: "ptz", label: "PTZ" },
  { v: "cube", label: "Cube" }, { v: "fisheye", label: "Fisheye" },
];
const CONNECTIVITY = [
  { v: "wired", label: "Wired" }, { v: "wireless", label: "Wireless" },
  { v: "4g", label: "4G / SIM" }, { v: "solar", label: "Solar" },
];

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN')}`;

const TAG_BADGES = {
  best_seller: { label: "★ Best Seller", color: "bg-amber-500 text-white" },
  hot_deal: { label: "🔥 Hot Deal", color: "bg-red-600 text-white" },
  new_arrival: { label: "✨ New", color: "bg-emerald-500 text-white" },
  featured: { label: "⭐ Featured", color: "bg-[#002FA7] text-white" },
};

const ProductCatalogue = ({ user, logout }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile filter drawer
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [quoteQty, setQuoteQty] = useState(1);

  // Filter state (synced to URL)
  const filters = useMemo(() => ({
    category: searchParams.get("category") || "",
    subcategory: searchParams.get("subcategory") || "",
    brand_id: searchParams.get("brand_id") || "",
    product_type: searchParams.get("product_type") || "",
    camera_type: searchParams.get("camera_type") || "",
    resolution: searchParams.get("resolution") || "",
    indoor_outdoor: searchParams.get("indoor_outdoor") || "",
    connectivity: searchParams.get("connectivity") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    tag: searchParams.get("tag") || "",
    search: searchParams.get("search") || "",
    sort: searchParams.get("sort") || "featured",
  }), [searchParams]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => setSearchParams(filters.search ? { search: filters.search } : {});

  useEffect(() => {
    axios.get(`${API}/product-categories`).then(r => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data || [])).catch(() => {});
    getCompanySettings(); // prime cache for WhatsApp wa.me links
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/products`, { params });
      setProducts(r.data || []);
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => v && !["search", "sort"].includes(k)).length;
  const currentCategory = categories.find(c => c.slug === filters.category);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <SEO title="Product Catalogue — SwifTech India" description="Shop CCTV cameras, DVR, NVR, biometrics, home automation and security systems at dealer-friendly prices." />

      <div className="flex-1 overflow-auto">
        {user?.role === "dealer" && user?.dealer_status === "approved" && (
          <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900" data-testid="dealer-welcome-banner">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
              <ShieldCheck size={18} weight="fill" />
              <span className="text-sm font-bold">Dealer Account Active</span>
              <span className="text-xs">You're seeing exclusive dealer pricing across the catalogue.</span>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/dealers/price-list.pdf?requester_role=dealer&requester_email=${encodeURIComponent(user.email)}`} target="_blank" rel="noopener noreferrer"
                className="ml-auto bg-slate-900 text-amber-400 hover:bg-black px-3 py-1 text-xs font-bold flex items-center gap-1.5" data-testid="dealer-price-list-btn">
                ⬇ Download Price List PDF
              </a>
            </div>
          </div>
        )}
        {/* Top hero with search */}
        <div className="bg-gradient-to-br from-[#001a5c] to-[#002FA7] text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-[11px] uppercase tracking-[0.3em] text-blue-200 mb-1">Catalogue</div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>Security Systems · CCTV · Automation</h1>
            <p className="text-sm md:text-base text-blue-100 mb-6 max-w-2xl">Browse 100+ products from HIKVISION, CP Plus, Dahua & more. Dealer pricing on 10+ units, bulk pricing on 50+.</p>
            <div className="relative max-w-2xl">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="catalogue-search"
                value={filters.search}
                onChange={e => setFilter("search", e.target.value)}
                placeholder="Search camera, DVR, biometric, smart lock…"
                className="w-full bg-white text-slate-900 rounded-none pl-11 pr-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          {/* Category ribbon */}
          <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 py-4 flex gap-3 overflow-x-auto scrollbar-none">
              <button
                onClick={() => { setFilter("category", ""); setFilter("subcategory", ""); }}
                className={`shrink-0 px-4 py-2 rounded text-xs font-bold tracking-wider uppercase ${!filters.category ? "bg-amber-400 text-slate-900" : "bg-white/10 hover:bg-white/20 text-white"}`}
                data-testid="cat-all"
              >
                All Products
              </button>
              {categories.map(c => {
                const Icon = CATEGORY_ICONS[c.icon] || ShieldCheck;
                return (
                  <button
                    key={c.slug}
                    data-testid={`cat-${c.slug}`}
                    onClick={() => { setFilter("category", c.slug); setFilter("subcategory", ""); }}
                    className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded text-xs font-bold tracking-wider uppercase transition-all ${filters.category === c.slug ? "bg-amber-400 text-slate-900" : "bg-white/10 hover:bg-white/20 text-white"}`}
                  >
                    <Icon size={14} weight={filters.category === c.slug ? "fill" : "regular"} />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Subcategory chips (when a cat is selected) */}
          {currentCategory && (
            <div className="flex gap-2 flex-wrap mb-5">
              {currentCategory.subcategories.map(s => (
                <button
                  key={s.slug}
                  data-testid={`subcat-${s.slug}`}
                  onClick={() => setFilter("subcategory", filters.subcategory === s.slug ? "" : s.slug)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${filters.subcategory === s.slug ? "bg-[#002FA7] border-[#002FA7] text-white" : "border-slate-300 text-slate-700 hover:border-[#002FA7]"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Sidebar filters */}
            <aside className={`bg-white border border-slate-200 rounded p-4 h-fit sticky top-4 ${sidebarOpen ? "" : "hidden lg:block"}`} data-testid="filters-sidebar">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-600">Filters {activeFiltersCount > 0 && <span className="ml-1 bg-[#002FA7] text-white px-1.5 rounded">{activeFiltersCount}</span>}</div>
                {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-[11px] text-red-600 hover:underline" data-testid="clear-filters">Clear all</button>}
              </div>

              <FilterGroup title="Brand">
                <select value={filters.brand_id} onChange={e => setFilter("brand_id", e.target.value)} data-testid="filter-brand" className="w-full text-sm p-2 border border-slate-200 rounded outline-none focus:border-[#002FA7] bg-white">
                  <option value="">All brands</option>
                  {brands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
                </select>
              </FilterGroup>

              <FilterGroup title="Product Type">
                <PillGroup options={PRODUCT_TYPES} value={filters.product_type} onChange={v => setFilter("product_type", v)} testPrefix="ptype" />
              </FilterGroup>

              <FilterGroup title="Camera Type">
                <PillGroup options={CAMERA_TYPES} value={filters.camera_type} onChange={v => setFilter("camera_type", v)} testPrefix="ctype" />
              </FilterGroup>

              <FilterGroup title="Resolution">
                <PillGroup options={RESOLUTIONS.map(r => ({ v: r, label: r.toUpperCase() }))} value={filters.resolution} onChange={v => setFilter("resolution", v)} testPrefix="res" />
              </FilterGroup>

              <FilterGroup title="Placement">
                <PillGroup options={[{v: "indoor", label: "Indoor"}, {v: "outdoor", label: "Outdoor"}]} value={filters.indoor_outdoor} onChange={v => setFilter("indoor_outdoor", v)} testPrefix="io" />
              </FilterGroup>

              <FilterGroup title="Connectivity">
                <PillGroup options={CONNECTIVITY} value={filters.connectivity} onChange={v => setFilter("connectivity", v)} testPrefix="conn" />
              </FilterGroup>

              <FilterGroup title="Price Range (₹)">
                <div className="flex gap-2">
                  <input type="number" placeholder="Min" value={filters.min_price} onChange={e => setFilter("min_price", e.target.value)} data-testid="filter-min-price"
                    className="w-full text-sm p-2 border border-slate-200 rounded outline-none focus:border-[#002FA7]" />
                  <input type="number" placeholder="Max" value={filters.max_price} onChange={e => setFilter("max_price", e.target.value)} data-testid="filter-max-price"
                    className="w-full text-sm p-2 border border-slate-200 rounded outline-none focus:border-[#002FA7]" />
                </div>
              </FilterGroup>

              <FilterGroup title="Tags">
                <PillGroup options={[
                  { v: "best_seller", label: "Best Seller" },
                  { v: "hot_deal", label: "Hot Deal" },
                  { v: "new_arrival", label: "New" },
                  { v: "featured", label: "Featured" },
                ]} value={filters.tag} onChange={v => setFilter("tag", v)} testPrefix="tag" />
              </FilterGroup>
            </aside>

            {/* Main */}
            <main>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="text-sm text-slate-600">
                  <span className="font-bold text-slate-900">{products.length}</span> product{products.length !== 1 ? "s" : ""} {filters.search && <>for <span className="italic">"{filters.search}"</span></>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSidebarOpen(o => !o)} className="lg:hidden border border-slate-300 px-3 py-2 text-sm flex items-center gap-1.5" data-testid="toggle-filters-mobile">
                    <Funnel size={14} /> Filters
                  </button>
                  <select value={filters.sort} onChange={e => setFilter("sort", e.target.value)} data-testid="sort-select"
                    className="border border-slate-300 p-2 text-sm bg-white outline-none focus:border-[#002FA7]">
                    <option value="featured">Featured</option>
                    <option value="new">New Arrivals</option>
                    <option value="best_selling">Best Selling</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                  </select>
                  <div className="border border-slate-300 flex">
                    <button onClick={() => setViewMode("grid")} className={`p-2 ${viewMode === "grid" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`} title="Grid view" data-testid="view-grid"><GridNine size={14} /></button>
                    <button onClick={() => setViewMode("list")} className={`p-2 ${viewMode === "list" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`} title="List view" data-testid="view-list"><List size={14} /></button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="py-20 flex justify-center"><div className="loading-spinner" /></div>
              ) : products.length === 0 ? (
                <div className="border border-dashed border-slate-300 py-20 text-center bg-white">
                  <ShieldCheck size={40} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No products match these filters.</p>
                  {activeFiltersCount > 0 && <button onClick={clearFilters} className="mt-3 text-sm text-[#002FA7] hover:underline font-bold">Clear filters</button>}
                </div>
              ) : viewMode === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {products.map(p => <ProductCard key={p.product_id} product={p} user={user} onView={() => navigate(`/products/${p.product_id}`)} onQuote={(qty) => { setQuoteProduct(p); setQuoteQty(qty || 1); }} />)}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {products.map(p => <ProductRow key={p.product_id} product={p} user={user} onView={() => navigate(`/products/${p.product_id}`)} onQuote={(qty) => { setQuoteProduct(p); setQuoteQty(qty || 1); }} />)}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
      <LeadCaptureModal
        open={!!quoteProduct}
        onClose={() => setQuoteProduct(null)}
        product={quoteProduct}
        user={user}
        defaultQuantity={quoteQty}
        source="catalogue_quick_quote"
      />
    </div>
  );
};

/* -------- Filter UI helpers -------- */
const FilterGroup = ({ title, children }) => (
  <div className="mb-4 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);

const PillGroup = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(o => (
      <button
        key={o.v}
        data-testid={`${testPrefix}-${o.v}`}
        onClick={() => onChange(value === o.v ? "" : o.v)}
        className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${value === o.v ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/* -------- Product Card (grid) -------- */
const ProductCard = ({ product, user, onView, onQuote }) => {
  const isDealer = user?.role === "dealer" && user?.dealer_status === "approved";
  // Dealer sees dealer_price as the headline price
  const retail = product.retail_price || product.price;
  const headline = isDealer
    ? (product.dealer_price || retail)
    : (product.offer_price && product.offer_price < retail ? product.offer_price : retail);
  const hasOffer = !isDealer && product.offer_price && product.offer_price > 0 && product.offer_price < retail;
  const dealerSaving = isDealer && retail > product.dealer_price ? (retail - product.dealer_price) : 0;
  const image = product.image_urls?.[0] || product.image_url || "/assets/placeholder-product.png";
  const keyTag = (product.tags || [])[0];
  const badge = keyTag ? TAG_BADGES[keyTag] : null;
  const inStock = product.stock_status !== "out_of_stock";

  const wa = (e) => {
    e.stopPropagation();
    window.open(buildProductEnquiryWaLink(product, 1), "_blank", "noopener");
  };

  return (
    <div data-testid={`product-card-${product.product_id}`} className="bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow-lg transition-all group cursor-pointer" onClick={onView}>
      <div className="aspect-square bg-slate-50 overflow-hidden relative">
        <img src={image} alt={product.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300" loading="lazy"
          onError={e => { e.target.src = "/assets/placeholder-product.png"; }} />
        {badge && <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 tracking-wider ${badge.color}`}>{badge.label}</span>}
        {hasOffer && <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 bg-red-600 text-white tracking-wider">SAVE {Math.round(((product.retail_price - product.offer_price) / product.retail_price) * 100)}%</span>}
        {!inStock && <span className="absolute bottom-2 left-2 text-[10px] font-bold px-2 py-0.5 bg-slate-900 text-white tracking-wider">OUT OF STOCK</span>}
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-start gap-1">
          {product.brand_name
            ? <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex-1 min-w-0 truncate">{product.brand_name}</div>
            : <div className="flex-1" />}
          <OpenInTabLink to={`/products/${product.product_id}`} testId={`card-open-tab-${product.product_id}`} size={13} />
        </div>
        <h3 className="text-sm font-bold line-clamp-2 leading-snug" style={{fontFamily: 'Outfit, sans-serif'}}>{product.name}</h3>
        <div className="flex flex-wrap gap-1 text-[10px] text-slate-600">
          {product.resolution && <span className="bg-slate-100 px-1.5 py-0.5">{product.resolution.toUpperCase()}</span>}
          {product.camera_type && <span className="bg-slate-100 px-1.5 py-0.5 capitalize">{product.camera_type}</span>}
          {product.indoor_outdoor && <span className="bg-slate-100 px-1.5 py-0.5 capitalize">{product.indoor_outdoor}</span>}
        </div>
        <div className="pt-1.5 flex items-baseline gap-2 flex-wrap">
          <span className="text-base font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>{headline > 0 ? fmtInr(headline) : "Get Best Price"}</span>
          {isDealer && dealerSaving > 0 && <span className="text-[9px] bg-amber-400 text-slate-900 px-1 py-0.5 font-bold uppercase tracking-wider">Dealer</span>}
          {hasOffer && <span className="text-[11px] line-through text-slate-400">{fmtInr(product.retail_price)}</span>}
        </div>
        <div className="grid grid-cols-3 gap-1 pt-2">
          <button onClick={(e) => { e.stopPropagation(); onView(); }} title="View details" data-testid={`card-view-${product.product_id}`} className="border border-slate-300 hover:bg-slate-50 py-1.5 flex items-center justify-center"><Eye size={12} /></button>
          <button onClick={wa} title="WhatsApp" data-testid={`card-whatsapp-${product.product_id}`} className="border border-green-200 hover:bg-green-50 text-green-700 py-1.5 flex items-center justify-center"><WhatsappLogo size={12} weight="fill" /></button>
          <button onClick={(e) => { e.stopPropagation(); onQuote?.(1); }} title="Get Quote" data-testid={`card-quote-${product.product_id}`} className="border border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#00227A] py-1.5 text-[10px] font-bold flex items-center justify-center gap-1">QUOTE</button>
        </div>
      </div>
    </div>
  );
};

/* -------- Product Row (list) -------- */
const ProductRow = ({ product, user, onView, onQuote }) => {
  const price = product.offer_price && product.offer_price < (product.retail_price || product.price) ? product.offer_price : (product.retail_price || product.price);
  const image = product.image_urls?.[0] || product.image_url || "/assets/placeholder-product.png";
  const keyTag = (product.tags || [])[0];
  const badge = keyTag ? TAG_BADGES[keyTag] : null;
  const wa = (e) => {
    e.stopPropagation();
    window.open(buildProductEnquiryWaLink(product, 1), "_blank", "noopener");
  };
  return (
    <div data-testid={`product-row-${product.product_id}`} onClick={onView} className="bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow p-3 flex gap-4 cursor-pointer transition-all">
      <div className="w-32 h-32 bg-slate-50 shrink-0 relative">
        <img src={image} alt={product.name} className="w-full h-full object-contain p-1" onError={e => { e.target.src = "/assets/placeholder-product.png"; }} />
        {badge && <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 tracking-wider ${badge.color}`}>{badge.label}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-1">
          {product.brand_name && <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex-1">{product.brand_name}</div>}
          <OpenInTabLink to={`/products/${product.product_id}`} testId={`row-open-tab-${product.product_id}`} size={13} />
        </div>
        <h3 className="text-base font-bold mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{product.name}</h3>
        <p className="text-xs text-slate-600 line-clamp-2 mb-2">{product.description}</p>
        <div className="flex flex-wrap gap-1.5 text-[11px] text-slate-600">
          {product.resolution && <span className="bg-slate-100 px-2 py-0.5">{product.resolution.toUpperCase()}</span>}
          {product.camera_type && <span className="bg-slate-100 px-2 py-0.5 capitalize">{product.camera_type}</span>}
          {product.night_vision_range && <span className="bg-slate-100 px-2 py-0.5">Night: {product.night_vision_range}</span>}
          {product.warranty && <span className="bg-slate-100 px-2 py-0.5">Warranty: {product.warranty}</span>}
        </div>
      </div>
      <div className="w-48 shrink-0 flex flex-col items-end justify-between">
        <div className="text-right">
          <div className="text-xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>{price > 0 ? fmtInr(price) : "Get Quote"}</div>
          {product.dealer_price > 0 && product.dealer_price !== price && <div className="text-[10px] text-slate-500">Dealer: {fmtInr(product.dealer_price)}</div>}
          {product.bulk_price > 0 && product.bulk_price !== price && <div className="text-[10px] text-slate-500">Bulk: {fmtInr(product.bulk_price)}</div>}
        </div>
        <div className="flex gap-1 w-full mt-2">
          <button onClick={wa} className="flex-1 bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold py-1.5 flex items-center justify-center gap-1"><WhatsappLogo size={12} weight="fill" /> WA</button>
          <button onClick={(e) => { e.stopPropagation(); onQuote?.(1); }} className="flex-1 bg-[#002FA7] hover:bg-[#00227A] text-white text-[11px] font-bold py-1.5 flex items-center justify-center gap-1"><PaperPlaneTilt size={12} weight="fill" /> Quote</button>
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogue;
